import React, { useMemo, useCallback } from 'react';
import { ThemeProvider, withStyles } from '@material-ui/styles';
import flowRight from 'lodash.flowright';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import locale from 'date-fns/locale/en-US';
import { createTheme, Tooltip } from '@material-ui/core';
import clsx from 'clsx';

import styles from './styles';
import Optional from '../../utils/optional';
import {
  findFirstAvailableDay,
  checkNonWorkingDays
} from '../../utils/dateUtils';
import withSettings from '../../utils/withSettings';
import { customPickerStyles } from '../../modals/editSchedule/styles/datePickers';
import { translate } from '../../i18n/I18nProvider';

const DANISH_COUNTRY_CODE = 'DK';

const DatePickerRow = ({
  classes,
  name,
  label = 'EMPTY',
  labelText,
  showLabel = true,
  handleBlur,
  setFieldValue,
  values = {},
  errors = {},
  customStyles = {},
  disabled = false,
  daysAhead = 0,
  settings,
  infoIcon = false,
  infoIconTooltipIntlKey = 'EMPTY',
  infoIconTooltipContent = null,
  mobile = false,
  disablePast = true,
  showOnlyAvailable = true,
  isRequired = false,
  format = 'dd/MM/yyyy',
  placeholder = 'dd/MM/yyyy',
  key = 'name',
  disableWeekends = true,
  readOnly = true,
  shouldDisableDate = true
}) => {
  const firstAvailableDay = useMemo(
    () => findFirstAvailableDay(daysAhead, DANISH_COUNTRY_CODE),
    [daysAhead]
  );

  const title = useMemo(
    () => Optional(labelText).or(translate(label)),
    // eslint-disable-next-line
    [labelText, label]
  );

  if (locale && locale.options) {
    locale.options.weekStartsOn = 1;
  }

  const handleChange = useCallback(
    selected => {
      setFieldValue(name, selected);
    },
    // eslint-disable-next-line
    []
  );

  const errorMessage = Optional(errors[name]).or(null);

  const materialDateTheme = useMemo(() => {
    return materialDatePickerTheme({
      settings,
      MuiOutlinedInputRootWidth: customStyles?.MuiOutlinedInputRootWidth
    });
  }, [settings, customStyles?.MuiOutlinedInputRootWidth]);

  return (
    <div className={classes.root} style={customStyles.root} key={key}>
      <div className={classes.field}>
        <div
          className={clsx(classes.label, {
            [classes.mobileLabel]: mobile
          })}
          style={customStyles.label}
        >
          {showLabel && title}
          {infoIcon && (
            <Tooltip
              title={
                infoIconTooltipContent !== null
                  ? infoIconTooltipContent
                  : translate(infoIconTooltipIntlKey)
              }
            >
              <i className={clsx('fas fa-info-circle', classes.infoIcon)}></i>
            </Tooltip>
          )}
        </div>

        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
          <ThemeProvider theme={materialDateTheme}>
            <div className={classes.valueContainer}>
              <div className={classes.requiredIcon}>
                {isRequired ? '*' : ''}
              </div>
              <KeyboardDatePicker
                inputVariant="outlined"
                margin="dense"
                className={classes.fieldValue}
                name={name}
                value={Optional(values[name]).or(null)}
                onChange={handleChange}
                disabled={disabled}
                disablePast={disablePast}
                autoOk={true}
                format={format}
                placeholder={placeholder}
                minDate={showOnlyAvailable ? firstAvailableDay : new Date(0)}
                InputProps={{
                  readOnly
                }}
                clearable
                showTodayButton
                shouldDisableDate={
                  shouldDisableDate
                    ? date =>
                        checkNonWorkingDays({
                          date,
                          countryCode: DANISH_COUNTRY_CODE,
                          disableWeekends
                        })
                    : void 0
                }
              />
            </div>
          </ThemeProvider>
        </MuiPickersUtilsProvider>
      </div>
      <div className={classes.errorMessage}>{errorMessage}</div>
    </div>
  );
};

export default flowRight(withSettings, withStyles(styles))(DatePickerRow);

const materialDatePickerTheme = ({
  settings,
  MuiOutlinedInputRootWidth = '200px'
}) => {
  return createTheme({
    overrides: {
      MuiTextField: {
        root: {
          width: '210px',
          marginRight: '20px'
        }
      },
      MuiOutlinedInput: {
        root: {
          width: MuiOutlinedInputRootWidth,
          padding: '0',
          marginRight: '30px'
        },
        input: {
          padding: '10px'
        },
        adornedEnd: {
          paddingRight: '0'
        }
      },
      ...customPickerStyles(settings)
    }
  });
};
